import React, { Dispatch, SetStateAction } from 'react';
import { Flex, Table, Modal, Collapse, Space, Tag, TableColumnsType } from 'antd';
import { extractWarningsFromJobResult } from '../utils/batchJobWarnings';
import { BatchJobsNestedTable } from '../BatchJobsStyledComponents';

interface DataType {
	"key": React.Key;
	"plan": string;
	"batchJobId": string;
	"duration": string;
	"unfilled time": string;
	"total dollars": string;
	"unfilled dates": { planName: string; unfilledDates: any[] };
}

function formatDuration(seconds: number | undefined): string {
	if (seconds == null || seconds <= 0) return '';
	const m = Math.floor(seconds / 60);
	const s = Math.round(seconds % 60);
	if (m === 0) return `${s}s`;
	return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

interface ResultsColumnProps {
	json_string: string;
	record: any;
	modalTitle: string;
	modalRows: any[];
	isModalVisible: boolean;
	onModalOk: () => void;
	onModalCancel: () => void;
	onSetModalBatchJobId: (jobId: string) => void;
	onSetModalTitle: (title: string) => void;
	onSetModalRows: Dispatch<SetStateAction<any[]>>;
	onSetIsModalVisible: (visible: boolean) => void;
	strategiesObj?: any; // Pass strategiesObj from parent to avoid re-parsing
}

const modalColumns = [
	{
		title: "Slot_ID",
		dataIndex: "slot_id",
		key: "slot_id",
		sorter: (a: any, b: any) =>
			parseInt(a.slot_id.split("_")[1]) - parseInt(b.slot_id.split("_")[1]),
		sortDirections: ["ascend" as const, "descend" as const, "ascend" as const],
	},
	{
		title: "Date",
		dataIndex: "date_str",
		key: "date_str",
	},
	{
		title: "Unfilled Time",
		dataIndex: "unfilledTime",
		key: "unfilledTime",
		sorter: (a: any, b: any) => parseInt(a.unfilledTime) - parseInt(b.unfilledTime),
		defaultSortOrder: "ascend" as const,
		sortDirections: ["ascend" as const, "descend" as const, "ascend" as const],
	},
];

/**
 * Component for rendering the Results column in the batch jobs table
 */
export const ResultsColumn: React.FC<ResultsColumnProps> = ({
	json_string,
	record,
	modalTitle,
	modalRows,
	isModalVisible,
	onModalOk,
	onModalCancel,
	onSetModalBatchJobId,
	onSetModalTitle,
	onSetModalRows,
	onSetIsModalVisible,
	strategiesObj: strategiesObjProp,
}) => {
	if (!json_string || json_string.length === 0) {
		return null;
	}

	try {
		const json_result = JSON.parse(json_string);

		// Extract warnings using shared utility function
		const allWarnings = extractWarningsFromJobResult(json_result);

		// eslint-disable-next-line prefer-const
		let data: DataType[] = [];
		let i = 0;
		
		// Handle two formats:
		// 1. Syndication multi-strategy: json_result.strategies (with unfilledSummaryData)
		// 2. Broadcast: json_result.data
		const strategiesObj = strategiesObjProp || json_result.strategies || json_result.data;
		
		for (const planName in strategiesObj) {
			const unfilledDatesSet = new Set();
			i++;
			const row = strategiesObj[planName];
			
			// Syndication format: data in unfilledSummaryData
			// Broadcast format: data at top level
			const slotsWithUnfilledTime = row.unfilledSummaryData?.slots_with_unfilled_time || row.slots_with_unfilled_time || [];
			
			for (let j = 0; j < slotsWithUnfilledTime.length; j++) {
				const slot = slotsWithUnfilledTime[j];
				unfilledDatesSet.add(slot.date_str);
			}
			
			// Syndication: totalUnfilledTime and totalDollars at top level
			// Broadcast: unfilledTime and totalDollars at top level
			const unfilledTime = row.totalUnfilledTime ?? row.unfilledTime ?? 0;
			const totalDollars = row.totalDollars ?? 0;
			
			data.push({
				"key": i,
				"batchJobId": record.id,
				"plan": planName,
				"duration": formatDuration(row.durationSeconds),
				"unfilled time": unfilledTime,
				"total dollars": totalDollars,
				"unfilled dates": {
					planName: planName,
					unfilledDates: Array.from(unfilledDatesSet),
				},
			});
		}

		const columns: TableColumnsType<DataType> = [
			{
				title: "Plan",
				dataIndex: "plan",
				key: "plan",
				width: "30%",
			},
			{
				title: "Duration",
				dataIndex: "duration",
				key: "duration",
				width: "12%",
			},
			{
				title: "Unfilled Time",
				dataIndex: "unfilled time",
				key: "unfilled time",
				width: "14%",
			},
			{
				title: "Dollars",
				dataIndex: "total dollars",
				key: "dollars",
				width: "12%",
			},
			{
				title: "Unfilled Dates",
				dataIndex: "unfilled dates",
				key: "unfilled dates",
				render: (data: any, record: any) => {
					const planName = data.planName;
					const dates = data.unfilledDates;
					const yyyyMMDDPartsStringArr: string[] = [];
					for (let i = 0; i < dates.length; i++) {
						const yyyyMMDDParts = dates[i].split("-");
						yyyyMMDDPartsStringArr.push(yyyyMMDDParts[1] + "/" + yyyyMMDDParts[2]);
					}
					return (
						<a
							onClick={() => {
								onSetModalBatchJobId(record.batchJobId);
								
								const currentStrategy = strategiesObj[planName];
								
								// Syndication: totalUnfilledTime at top level
								// Broadcast: unfilledTime at top level
								const unfilledTimeValue = currentStrategy.totalUnfilledTime ?? currentStrategy.unfilledTime ?? 0;
								
								// Syndication: slots in unfilledSummaryData
								// Broadcast: slots at top level
								const slotsData = currentStrategy.unfilledSummaryData?.slots_with_unfilled_time 
									|| currentStrategy.slots_with_unfilled_time 
									|| [];
								
								onSetModalTitle(
									planName + " = " + unfilledTimeValue + " seconds"
								);
								onSetModalRows(slotsData);
								onSetIsModalVisible(true);
							}}
							className="regular-link"
						>
							{" "}
							{yyyyMMDDPartsStringArr.join(",")}{" "}
						</a>
					);
				},
			},
		];

		if (data.length === 0) {
			return null;
		}

		return (
			<Flex vertical={true} style={{ width: '100%' }}>
				{allWarnings.length > 0 && (
					<Collapse
						items={[{
							key: 'warnings',
							label: (
								<span style={{ fontWeight: 600, fontSize: '14px', color: '#faad14' }}>
									⚠️ Warnings ({allWarnings.length})
								</span>
							),
							children: (
								<Space direction="vertical" style={{ width: "100%" }} size="small">
									{allWarnings.map((warning, idx) => (
										<div key={idx} style={{ fontSize: '12px' }}>
											<Tag color="warning" style={{ marginRight: 4, fontSize: '11px' }}>
												{warning.type}
											</Tag>
											{warning.message}
										</div>
									))}
								</Space>
							),
							style: { borderColor: '#faad14', backgroundColor: '#fffbe6' }
						}]}
						style={{ marginBottom: 12 }}
					/>
				)}
				<BatchJobsNestedTable<DataType>
					columns={columns}
					dataSource={data}
					pagination={false}
					size="small"
				/>
				<Modal
					title={modalTitle}
					open={isModalVisible}
					onOk={onModalOk}
					onCancel={onModalCancel}
				>
					<Table
						columns={modalColumns}
						dataSource={modalRows}
						pagination={false}
						scroll={{ y: 540 }}
					/>
				</Modal>
			</Flex>
		);
	} catch (error) {
		console.error("Error parsing job_result_json:", error);
		return null;
	}
};
